import { ChangeEvent, FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { FieldErrors, UseFormSetValue } from "react-hook-form";

import { InputField } from "@/components/InputField/InputField";
import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";

import { CreateLiPost } from "../schema/liPost";
import { PostPreviewCard } from "./PostPreviewCard";
import { PostUrlValidation } from "./usePostUrlValidation";

interface PostUrlSectionProps {
	formValues: Partial<CreateLiPost>;
	setValue: UseFormSetValue<CreateLiPost>;
	errors: FieldErrors<CreateLiPost>;
	validation: PostUrlValidation;
}

/** BY_URL source: enter a LinkedIn post URL, validate it, and preview the post. */
export const PostUrlSection: FunctionComponent<PostUrlSectionProps> = ({
	formValues,
	setValue,
	errors,
	validation,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`liPost.${key}`);
	const { postPreview, validateError, isValidating, validate, setPostPreview } = validation;

	return (
		<div className="flex flex-col w-full mb-8">
			<div className="flex flex-row gap-4 w-full items-end">
				<div className="flex-1">
					<InputField
						className="-mb-3"
						error={!!errors.postUrl?.message || !!validateError}
						handleChange={(event: ChangeEvent<HTMLInputElement>) => {
							setValue("postUrl", event.target.value, {
								shouldDirty: true,
								shouldValidate: true,
							});
						}}
						isDisabled={false}
						label={"Enter Linkedin Post URL"}
						name={"postUrl"}
						placeholder={ts("postUrlPlaceholder")}
						showError={true}
						value={formValues?.postUrl || ""}
					/>
				</div>
				<div className="flex mt-6">
					<Button
						color={ButtonColor.ACTION_SECONDARY}
						disabled={
							!formValues.postUrl ||
							isValidating ||
							!formValues.postUrl?.includes("www.linkedin.com/posts")
						}
						isLoading={isValidating}
						size={ButtonSize.L}
						title={t("liPost.validateUrl")}
						onClick={() => validate(formValues.postUrl)}
					/>
				</div>
			</div>
			{validateError && <div className="text-xs text-red-500 mt-1">{validateError}</div>}
			{postPreview && (
				<div className="mt-4">
					<PostPreviewCard data={postPreview} onDismiss={() => setPostPreview(null)} />
				</div>
			)}
		</div>
	);
};
